import {
  Autocomplete,
  Box,
  Chip,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { Dispatch, SetStateAction, useContext, useState } from "react";
import {
  BrandDealJourney,
  BrandDealJourneyDisplayName,
  DiscoverySource,
  DiscoverySourceDisplayName,
  ProfileType,
} from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";
import { routes } from "schemas/routes";

import BentoBrandsAutocomplete from "components/BentoBrandsAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import StyledTextField from "components/Styled/TextField";
import { isPage } from "utils/general";
import { makeDeepCopy } from "utils/updateLocalState";

import StyledOnboardingPage from "../StyledOnboardingPage";
import styles from "../styles";
import AboutFormLinks from "./Links";
import {
  SocialMediaFields,
  SocialMediaLinks,
  isValidUsername,
  sanitizeInput,
} from "./helpers";

interface AboutFormProps {
  handleNext: (onboardingForm?: Map) => void;
  handleSkip?: () => void;
  handleBack?: () => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  isVAFlow?: boolean;
  hideDiscoveryQuestion?: boolean;
}

const AboutForm = ({
  handleNext,
  handleSkip,
  handleBack,
  onboardingForm,
  setOnboardingForm,
  hideDiscoveryQuestion = false,
}: AboutFormProps) => {
  const isProfilePage = isPage(routes.profile);
  const { profileDialogOpen } = useContext(OnboardingDialogsContext);
  const { setAlert } = useContext(AlertContext);
  const [linksValidation, setLinksValidation] = useState<{
    [key in string]: string | undefined;
  }>({});
  const [links, setLinks] = useState<SocialMediaLinks[]>([]);
  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;
    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  const handleSetDiscoverSource = (source?: string) => {
    if (!source) return;
    const copy = makeDeepCopy(onboardingForm);
    copy.discoverySource = source;
    setOnboardingForm(copy);
  };

  const handleSetBrandDealJourney = (journey?: BrandDealJourney) => {
    if (!journey) return;
    const copy = makeDeepCopy(onboardingForm);
    copy.brandDealJourney = journey;
    setOnboardingForm(copy);
  };

  const handleSubmit = () => {
    if (isProfilePage || profileDialogOpen) {
      if (Object.values(linksValidation).some((v) => v !== undefined)) {
        return;
      }

      for (const key in onboardingForm) {
        if (key in SocialMediaFields && onboardingForm[key]) {
          const input = sanitizeInput(onboardingForm[key]);
          if (input && !isValidUsername(input) && key !== "portfolioUrl") {
            return;
          }
        }
      }
    }
    if (!onboardingForm?.name) {
      setAlert("Please fill in your name", "error");
      return;
    }
    if (profileDialogOpen && !onboardingForm?.location) {
      setAlert("Please fill in location", "error");
      return;
    }
    if (
      (!onboardingForm?.profileTypes ||
        onboardingForm?.profileTypes?.length === 0) &&
      !profileDialogOpen
    ) {
      setAlert("Please fill in who are you", "error");
      return;
    }
    if (
      !onboardingForm?.targetBrandTypes &&
      !isPage(routes.aboutSelfServeOnboarding) &&
      !profileDialogOpen
    ) {
      setAlert("Please fill in who do you want to pitch to", "error");
      return;
    }

    if (
      onboardingForm?.latitude &&
      onboardingForm?.longitude &&
      onboardingForm?.latitude === onboardingForm?.longitude
    ) {
      setAlert(
        "Cannot store this location, please retype the address and try again",
        "error",
      );
      setOnboardingForm((prev) => ({
        ...prev,
        latitude: undefined,
        longitude: undefined,
      }));
      return;
    }

    handleNext(onboardingForm);
  };

  const handleLocationChange = (userLocation: UserLocation) => {
    setOnboardingForm((prev) => ({
      ...prev,
      city: userLocation.address,
      location: userLocation.country_code,
      addressComponents: userLocation.address_components,
      longitude: userLocation.longitude,
      latitude: userLocation.latitude,
    }));
  };

  const updateWishlistBrands = (newBrands: string[]) => {
    setOnboardingForm((prev) => {
      return {
        ...prev,
        wishlistBrands: newBrands,
      };
    });
  };

  return (
    <>
      <StyledOnboardingPage
        title={"Let's Get To Know You ✍️"}
        handleBack={handleBack}
        handleNext={handleSubmit}
        handleSkip={handleSkip}
      >
        <StyledCard styles={{ px: 3, py: 4 }}>
          {!profileDialogOpen && (
            <Box sx={{ mb: 2 }}>
              <StyledTextField
                value={onboardingForm?.name || ""}
                name="name"
                placeholder="First Last"
                onChange={handleChange}
                label="Your Name"
                required
                fullWidth
              />
            </Box>
          )}

          {!profileDialogOpen && (
            <Box sx={{ mb: 2 }}>
              <Typography gutterBottom>Who are you? *</Typography>
              <Autocomplete
                multiple
                options={Object.values(ProfileType)}
                sx={{
                  "& fieldset.MuiOutlinedInput-notchedOutline": {
                    borderRadius: 4,
                    borderColor: grey[300],
                  },
                }}
                getOptionLabel={(option) => option}
                value={onboardingForm?.profileTypes || []}
                onChange={(_, newValue) => {
                  setOnboardingForm((p) => {
                    if (!p) return p;
                    const copy = { ...p };
                    copy.profileTypes = newValue;
                    return copy;
                  });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                      color="primary"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select all that apply" />
                )}
              />
              {onboardingForm?.profileTypes?.includes(ProfileType.other) && (
                <TextField
                  placeholder="Please specify"
                  value={onboardingForm?.otherType || ""}
                  onChange={(e: CustomEvent) => {
                    const { value } = e.target;
                    setOnboardingForm((p) => {
                      if (!p) return p;
                      const copy = { ...p };
                      copy.otherType = value;
                      return copy;
                    });
                  }}
                  variant="standard"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          {!profileDialogOpen && (
            <Box sx={{ mb: 2 }} display="flex" flexDirection="column">
              <Typography gutterBottom>
                Who do you want to pitch to? *
              </Typography>
              <StyledTextField
                value={onboardingForm?.targetBrandTypes || ""}
                onChange={(e: CustomEvent) => {
                  const { value } = e.target;
                  setOnboardingForm((p) => {
                    if (!p) return p;
                    const copy = { ...p };
                    copy.targetBrandTypes = value;
                    return copy;
                  });
                }}
                placeholder="Restaurant managers, PR team, Event sponsors, Influencers, Marketing managers..."
                variant="outlined"
                fullWidth
                multiline
              />
            </Box>
          )}

          {!profileDialogOpen && (
            <Box sx={styles.questionBox}>
              <Typography gutterBottom>
                Where are you in your brand deal journey?
              </Typography>
              <Typography variant="caption" color="textSecondary" paragraph>
                Select an option below.
              </Typography>

              <Grid container gap={1} sx={{ mb: 2 }} alignItems={"center"}>
                {Object.values(BrandDealJourney)?.map((journey) => (
                  <QuestionChip
                    isSelected={onboardingForm?.brandDealJourney === journey}
                    label={BrandDealJourneyDisplayName[journey]}
                    onClick={() => handleSetBrandDealJourney(journey)}
                    key={journey}
                  />
                ))}
              </Grid>
            </Box>
          )}

          {!isProfilePage && !hideDiscoveryQuestion && (
            <Box sx={styles.questionBox}>
              <Typography gutterBottom>How did you hear about us?</Typography>
              <Typography variant="caption" color="textSecondary" paragraph>
                Select an option below.
              </Typography>

              <Grid container gap={1} sx={{ mb: 2 }} alignItems={"center"}>
                {Object.values(DiscoverySource)?.map((source, index) => (
                  <QuestionChip
                    isSelected={onboardingForm?.discoverySource === source}
                    label={DiscoverySourceDisplayName[source]}
                    onClick={() => handleSetDiscoverSource(source)}
                    key={`discovery-source-${index}`}
                  />
                ))}
                {onboardingForm?.discoverySource === DiscoverySource.other && (
                  <TextField
                    variant="standard"
                    name="discoverySourceOther"
                    placeholder="Please specify"
                    onChange={handleChange}
                    value={onboardingForm?.discoverySourceOther || ""}
                  />
                )}
              </Grid>
            </Box>
          )}

          {(isProfilePage || profileDialogOpen) && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Location (city or zip code) *
                </Typography>
                <LocationAutocomplete
                  handleLocationChange={handleLocationChange}
                  userLocation={{
                    address: onboardingForm.city,
                    longitude: onboardingForm.longitude,
                    latitude: onboardingForm.latitude,
                    country_code: onboardingForm.location,
                    address_components: onboardingForm.addressComponents,
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography gutterBottom>
                  Enter a few dream brands you'd love to work with
                </Typography>
                <BentoBrandsAutocomplete
                  selectedBentoBrands={onboardingForm?.wishlistBrands || []}
                  updateSelectedBentoBrands={updateWishlistBrands}
                />
              </Box>
            </>
          )}

          {isProfilePage && (
            <AboutFormLinks
              onboardingForm={onboardingForm}
              setOnboardingForm={setOnboardingForm}
              setLinksValidation={setLinksValidation}
              links={links}
              setLinks={setLinks}
              validation={linksValidation}
            />
          )}
        </StyledCard>
      </StyledOnboardingPage>
    </>
  );
};

export default AboutForm;
